import React from 'react';
import { Box, Typography } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import type { CalibratedRange, MaturityDescriptor, Target } from '../../services/pours';
import type { IsoLike } from '../../utils/datetime';
import {
  TARGET_OUTSIDE_RANGE_LABEL,
  formatMaturityWithIndex,
  isTargetOutsideRange,
  maturityMeta,
  targetPurpose,
  targetStrengthText,
} from '../../utils/maturityDisplay';
import { targetStatusText } from '../../utils/targetStatus';

/** The location facts that turn each checkpoint's tooltip into a status
 *  ("Reached 22 Sep 2026, 05:25 AEST (11h 50m after concrete start)" or
 *  "Not yet: 120.3 °C·hrs to go"), as the dashboard's Targets card says it. */
export interface MaturityProgressDetail {
  /** The location's current maturity (TTF or equivalent age); missing
   *  counts as 0. */
  currentMaturity?: number | null;
  /** The location's concrete start (EAStart), naive UTC. */
  concreteStart?: IsoLike;
  /** The location's monitoring start, used when there is no concrete start. */
  monitoringStart?: IsoLike;
  /** The pour's IANA timezone; UTC when missing. */
  timezoneIana?: string | null;
  /** Monitoring has ended (pour completed or the location's logging ended). */
  ended?: boolean;
  /** The calibration's tested range, to flag targets on older responses that
   *  do not send outside_range. */
  range?: CalibratedRange | null;
}

interface MaturityProgressProps {
  targets: Target[];
  /** The company strength unit; left off the value while unknown, never
   *  guessed. */
  strengthUnit?: string | null;
  /** The pour's maturity naming, to name a target that has no strength by
   *  its maturity ("EA at 20 °C: 120.0 hrs"). */
  maturity?: MaturityDescriptor | null;
  /** When true, show a compact bar with progress summary (used in control-panel views). */
  showLabel?: boolean;
  /** When true, hide text labels and shrink checkpoints for narrow spaces (control panel). */
  compact?: boolean;
  /** When set (the dashboard location list), each checkpoint's tooltip adds
   *  the target's status and its outside-range flag. Without it the tooltip
   *  is the name and ", achieved" (control panel). */
  detail?: MaturityProgressDetail;
  sx?: { height?: number; [key: string]: any };
}

/** "Strip (15.0 MPa)", "15.0 MPa" without a purpose, the maturity when the
 *  target has no strength ("Lift (TTF 120.0 °C·hrs)"), else the purpose or
 *  "Target": as the location chart names its markers. */
const targetName = (
  target: Target,
  strengthUnit?: string | null,
  maturity?: MaturityDescriptor | null,
): string => {
  const purpose = targetPurpose(target.name);
  const value =
    targetStrengthText(target.target_strength, strengthUnit) ||
    (maturity && target.target_maturity > 0
      ? formatMaturityWithIndex(target.target_maturity, maturity, 1)
      : '');
  return purpose && value ? `${purpose} (${value})` : purpose || value || 'Target';
};

/** The checkpoint's native tooltip. Without `detail`: "Strip (15.0 MPa),
 *  achieved". With it, one fact per line: the name, the status line, and
 *  "Outside tested range" when the target is flagged. */
const targetTooltip = (
  target: Target,
  strengthUnit?: string | null,
  maturity?: MaturityDescriptor | null,
  detail?: MaturityProgressDetail,
): string => {
  const name = targetName(target, strengthUnit, maturity);
  if (!detail) return `${name}${target.achieved ? ', achieved' : ''}`;
  const status = targetStatusText({
    achieved: target.achieved,
    achievedAt: target.achieved_at,
    targetMaturity: target.target_maturity,
    currentMaturity: detail.currentMaturity,
    concreteStart: detail.concreteStart,
    monitoringStart: detail.monitoringStart,
    ended: detail.ended,
    meta: maturity ?? maturityMeta(null),
    timezoneIana: detail.timezoneIana,
  });
  const lines = [name, status.text];
  if (isTargetOutsideRange(target, detail.range)) lines.push(TARGET_OUTSIDE_RANGE_LABEL);
  return lines.join('\n');
};

/**
 * Milestone-checkpoint progress bar showing which targets have been achieved.
 *
 * A target is achieved only when the engine has stamped it (the location's
 * maturity reached the target's maturity threshold). Strength never decides
 * it here: comparing the location's maturity with a strength target ticked
 * targets within hours of placement.
 *
 * Full mode (default): horizontal track with named checkpoints.
 *   - Green check circles for achieved targets, gray hollow for unachieved.
 *   - Track line coloured green for achieved portions, gray for remaining.
 *   - Target names displayed inline beside each checkpoint.
 *   - Each checkpoint's tooltip names the target; with `detail` it also says
 *     when it was reached or how much maturity is still to go.
 *
 * Compact mode (showLabel=true): simple green fill bar with "X/Y targets" label.
 */
export const MaturityProgress: React.FC<MaturityProgressProps> = ({
  targets,
  strengthUnit,
  maturity,
  showLabel = false,
  compact = false,
  detail,
  sx = {},
}) => {
  const barHeight = sx?.height ?? 28;
  const { height: _h, ...restSx } = sx || {};

  const sortedTargets = [...targets].sort(
    (a, b) => (a.target_maturity ?? 0) - (b.target_maturity ?? 0),
  );
  const numTargets = sortedTargets.length;

  // ---------- Empty state ----------
  if (!numTargets) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', ...restSx }}>
        No targets
      </Typography>
    );
  }

  // ---------- Compact bar mode (Pours.tsx control panel) ----------
  if (showLabel) {
    const achieved = sortedTargets.filter((t) => t.achieved).length;
    const fillPct = numTargets > 0 ? (achieved / numTargets) * 100 : 0;

    return (
      <Box sx={{ width: '100%', ...restSx }}>
        <Box
          sx={{
            position: 'relative',
            height: barHeight,
            bgcolor: 'grey.200',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {fillPct > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                bgcolor: '#8BC34A',
                transition: 'width 0.4s ease',
              }}
              style={{ width: `${fillPct}%` }}
            />
          )}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={500}
              sx={{
                color: fillPct > 50 ? 'common.white' : 'common.black',
                fontSize: '0.7rem',
              }}
            >
              {achieved}/{numTargets} targets
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // ---------- Milestone checkpoint mode (LocationList) ----------

  const checkSize = compact ? 14 : 22;
  const startDotSize = compact ? 8 : 10;
  const isAchieved = (t: Target) => t.achieved;

  const getSegmentColor = (segIndex: number): string => {
    const GRAY = '#e0e0e0';
    const GREEN = '#8BC34A';
    return isAchieved(sortedTargets[segIndex]) ? GREEN : GRAY;
  };

  return (
    <Box sx={{ width: '100%', ...restSx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', height: barHeight, gap: 1 }}>
        {sortedTargets.map((target, index) => {
          const achieved = isAchieved(target);

          return (
            <Box
              key={index}
              title={targetTooltip(target, strengthUnit, maturity, detail)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                '&:hover .maturity-line': {
                  height: '5px !important',
                },
                '&:hover .maturity-dot': {
                  transform: 'scale(1.3)',
                },
                '&:hover .maturity-circle': {
                  transform: 'scale(1.3)',
                },
                '&:hover .maturity-label': {
                  fontWeight: 700,
                },
              }}
            >
              {/* ---- Start / separator dot ---- */}
              <Box
                className="maturity-dot"
                sx={{
                  width: startDotSize,
                  height: startDotSize,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: getSegmentColor(index),
                  transition: 'background-color 0.4s ease, transform 0.15s ease',
                }}
              />

              {/* ---- Segment ---- */}
              <Box
                className="maturity-line"
                sx={{
                  height: 3,
                  flex: 1,
                  minWidth: 12,
                  background: getSegmentColor(index),
                  transition: 'background 0.4s ease, height 0.15s ease',
                }}
              />

              {/* ---- Label (before circle) ---- */}
              {!compact && (
                <Typography
                  className="maturity-label"
                  variant="caption"
                  sx={{
                    ml: 0.5,
                    mr: 0.5,
                    fontSize: '0.6rem',
                    lineHeight: 1.2,
                    color: achieved ? 'success.main' : 'text.secondary',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'font-weight 0.15s ease, color 0.15s ease',
                  }}
                >
                  {target.name}
                </Typography>
              )}

              {/* ---- Circle ---- */}
              <Box
                className="maturity-circle"
                sx={{
                  width: checkSize,
                  height: checkSize,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: achieved ? '#8BC34A' : '#fff',
                  border: `2px solid ${achieved ? '#8BC34A' : '#bdbdbd'}`,
                  zIndex: 1,
                  transition: 'transform 0.15s ease',
                }}
              >
                {achieved && (
                  <CheckIcon sx={{ fontSize: compact ? 10 : 14, color: '#fff' }} />
                )}
              </Box>
            </Box>
          );
        })}

      </Box>
    </Box>
  );
};
